import React from 'react';
import { TrendingUp, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ChartGradientDefs,
  chartColorAt,
  parseChartDate,
  useChartCurrency,
  useChartGradientIds,
  useChartPointInteraction,
} from './chartTheme';

interface PricePoint {
  date: string;
  price: number;
  store: string;
}

interface ItemPriceHistoryLineChartProps {
  itemName: string;
  data: PricePoint[];
  currencySymbol?: string;
}

export const ItemPriceHistoryLineChart: React.FC<{ props: ItemPriceHistoryLineChartProps; isStreaming?: boolean }> = ({
  props,
  isStreaming
}) => {
  const { t, i18n } = useTranslation();
  const {
    itemName = t('common.item'),
    data = [],
  } = props || {};

  const activeCurrencySymbol = useChartCurrency(props);
  const gradientIds = useChartGradientIds();

  const validData = (Array.isArray(data) ? data : []).filter(
    d => d && typeof d.price === 'number' && Number.isFinite(d.price) && !Number.isNaN(parseChartDate(d.date).getTime())
  );

  const { activePoint: hoveredPoint, pointProps, clearSelection } = useChartPointInteraction(validData.length);

  if (validData.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('chat.tracking_price_trends', { name: itemName })}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('chat.no_price_history_found', { name: itemName })}</p>
      </div>
    );
  }

  const sortedData = [...validData].sort((a, b) => parseChartDate(a.date).getTime() - parseChartDate(b.date).getTime() || a.price - b.price);
  const maxPrice = Math.max(...sortedData.map(d => d.price));
  const minPrice = Math.min(...sortedData.map(d => d.price));
  const range = maxPrice - minPrice || 1;
  const padding = range * 0.2;
  const chartMax = maxPrice + padding;
  const chartMin = Math.max(0, minPrice - padding);
  const chartRange = chartMax - chartMin || 1;

  const width = 400;
  const height = 220;
  const paddingX = 18;
  const paddingY = 24;
  const chartWidth = width - (paddingX * 2);
  const chartHeight = height - (paddingY * 2);

  // A single point sits in the middle instead of pinned to the left edge.
  const step = sortedData.length > 1 ? chartWidth / (sortedData.length - 1) : 0;
  const points = sortedData.map((d, i) => {
    const x = sortedData.length > 1 ? paddingX + i * step : width / 2;
    const y = paddingY + chartHeight - ((d.price - chartMin) / chartRange) * chartHeight;
    return { x, y, data: d, color: chartColorAt(sortedData.length > 1 ? i / (sortedData.length - 1) : 0) };
  });

  const formatDate = (value: string, opts: Intl.DateTimeFormatOptions) => parseChartDate(value).toLocaleDateString(i18n.language, opts);

  // X-axis labels: skip repeats of the same day, and thin them out when crowded.
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));
  const showDateLabel = (i: number) =>
    (i % labelEvery === 0 || i === points.length - 1) &&
    (i === 0 || formatDate(points[i].data.date, { month: 'short', day: 'numeric' }) !== formatDate(points[i - 1].data.date, { month: 'short', day: 'numeric' }));

  const pathStr = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
  const areaPathStr = `M ${points[0].x},${height - paddingY} L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},${height - paddingY} Z`;
  const minIndex = points.findIndex(p => p.data.price === minPrice);
  const maxIndex = points.findIndex(p => p.data.price === maxPrice);

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-4 py-4 sm:px-5 bg-[#1a2b3c] flex justify-between items-center text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500 rounded-xl shadow-lg">
            <TrendingUp size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 leading-none mb-1">{t('dashboard.price_tracker')}</div>
            <p className="text-sm font-black text-white leading-tight">{itemName}</p>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 bg-white">
        <div className="relative w-full aspect-[20/11] mb-3">
          <svg onPointerDown={event => { if (!(event.target as Element).closest('[data-chart-point]')) clearSelection(); }} viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible">
            <ChartGradientDefs ids={gradientIds} x1={paddingX} x2={width - paddingX} top={paddingY} bottom={height - paddingY} />

            {[0, 0.5, 1].map((tick) => {
              const y = paddingY + chartHeight - (tick * chartHeight);
              return (
                <line key={tick} x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="4 4" />
              );
            })}

            {points.length > 1 && (
              <>
                <path d={areaPathStr} fill={`url(#${gradientIds.area})`} mask={`url(#${gradientIds.mask})`} />
                <path d={pathStr} fill="none" stroke={`url(#${gradientIds.line})`} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </>
            )}

            {/* Interactive Vertical Guide */}
            {hoveredPoint !== null && (
              <line
                x1={points[hoveredPoint].x}
                y1={paddingY}
                x2={points[hoveredPoint].x}
                y2={height - paddingY}
                stroke={points[hoveredPoint].color}
                strokeWidth="1"
                strokeDasharray="4 4"
                className="animate-in fade-in duration-300"
              />
            )}

            {/* Data Points and Labels */}
            {points.map((p, i) => (
              <g
                key={i}
                className="cursor-pointer outline-none"
                {...pointProps(i, `${formatDate(p.data.date, { month: 'short', day: 'numeric', year: 'numeric' })}: ${activeCurrencySymbol}${p.data.price.toFixed(2)}${p.data.store ? ` · ${p.data.store}` : ''}`)}
              >
                {/* Invisible hit area */}
                <circle cx={p.x} cy={p.y} r="12" fill="transparent" />

                {showDateLabel(i) && (
                  <text
                    x={p.x}
                    y={height - paddingY + 18}
                    textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
                    className="fill-gray-400 font-bold text-[8px] uppercase tracking-tighter"
                  >
                    {formatDate(p.data.date, { month: 'short', day: 'numeric' })}
                  </text>
                )}

                <circle
                  cx={p.x}
                  cy={p.y}
                  r={hoveredPoint === i ? 6 : 4}
                  fill={hoveredPoint === i ? p.color : "white"}
                  stroke={p.color}
                  strokeWidth="2.5"
                  className="transition-all duration-200"
                />

                {hoveredPoint === null && (i === 0 || i === points.length - 1 || i === minIndex || i === maxIndex) && (
                  <text x={p.x} y={p.y - 12} textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"} className="fill-[#1a2b3c] font-black text-[9px]">
                    {activeCurrencySymbol}{p.data.price.toFixed(2)}
                  </text>
                )}
              </g>
            ))}
          </svg>

          {/* Floating Rich Tooltip Overlay */}
          {hoveredPoint !== null && (
            <div
              className="absolute z-20 pointer-events-none bg-[#1a2b3c] text-white p-3 rounded-2xl max-w-[min(15rem,85%)] shadow-2xl border border-white/10 animate-in zoom-in-95 duration-200"
              style={{
                left: `${(points[hoveredPoint].x / width) * 100}%`,
                top: `${(points[hoveredPoint].y / height) * 100}%`,
                // Open toward the chart's interior so edge points don't get clipped by the card.
                transform: `translate(${points[hoveredPoint].x < width / 2 ? '-12px' : 'calc(-100% + 12px)'}, ${points[hoveredPoint].y < height * 0.4 ? '12px' : 'calc(-100% - 12px)'})`
              }}
            >
              <div className="space-y-1 min-w-[120px]">
                <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">
                  {formatDate(points[hoveredPoint].data.date, { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="text-sm font-black leading-none">{activeCurrencySymbol}{points[hoveredPoint].data.price.toFixed(2)}</p>

                {points[hoveredPoint].data.store && (
                  <div className="pt-1.5 mt-1.5 border-t border-white/10 flex items-center gap-1.5 text-gray-400">
                    <Tag size={10} className="text-emerald-500 shrink-0" />
                    <p className="text-[8px] font-bold uppercase break-words max-w-[180px]">{points[hoveredPoint].data.store}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-50">
           <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100">
              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('comparison.lowest_found')}</p>
              <p className="text-lg font-black text-emerald-600">{activeCurrencySymbol}{minPrice.toFixed(2)}</p>
           </div>
           <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100">
              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('comparison.highest_point')}</p>
              <p className="text-lg font-black text-rose-500">{activeCurrencySymbol}{maxPrice.toFixed(2)}</p>
           </div>
        </div>
      </div>
    </div>
  );
};
